import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  role: {
    id: string;
    name: string;
    description?: string;
    tablePermissions?: Record<string, string[]>;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  token: string | null;
  csrfToken: string | null;
  apiCall: (endpoint: string, options?: RequestInit) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API配置 - 使用Cloudflare Workers API
const API_BASE_URL = 'https://d1-mysql-manager-api.5212025.xyz';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  // 获取CSRF Token
  const fetchCSRFToken = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setCsrfToken(data.csrfToken);
        return data.csrfToken;
      }
    } catch (error) {
      console.error('获取CSRF Token失败:', error);
    }
    return null;
  };

  // API调用辅助函数
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // 获取CSRF Token（如果需要）
    let currentCsrfToken = csrfToken;
    if (!currentCsrfToken && options.method && !['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())) {
      currentCsrfToken = await fetchCSRFToken();
    }

    const config: RequestInit = {
      credentials: 'include', // 重要：包含cookies用于session
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(currentCsrfToken && { 'X-CSRF-Token': currentCsrfToken }),
        ...options.headers,
      },
      ...options,
    };

    console.log('API call:', url, config); // 调试日志

    const response = await fetch(url, config);
    
    // 检查响应头中是否有新的CSRF Token
    const newCsrfToken = response.headers.get('X-CSRF-Token');
    if (newCsrfToken && newCsrfToken !== csrfToken) {
      setCsrfToken(newCsrfToken);
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // 登录函数
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const data = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (data.token && data.user) {
        setToken(data.token);
        setUser({
          id: data.user.id,
          email: data.user.email,
          role: {
            id: data.user.role?.id || data.user.roleId || 'admin',
            name: data.user.role?.name || data.user.roleName || 'Administrator',
            description: data.user.role?.description || '',
            tablePermissions: data.user.role?.tablePermissions || {}
          }
        });
        localStorage.setItem('auth_token', data.token);
      } else {
        throw new Error('登录响应格式错误');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 登出函数
  const logout = async () => {
    try {
      // 调用后端登出API
      if (token) {
        await apiCall('/api/auth/logout', {
          method: 'POST',
        });
      }
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      setToken(null);
      setUser(null);
      setCsrfToken(null);
      localStorage.removeItem('auth_token');
    }
  };

  // 验证token有效性
  const verifyToken = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const data = await apiCall('/api/auth/me');
      setUser({
        id: data.user.id,
        email: data.user.email,
        role: {
          id: data.user.role?.id || data.user.roleId || 'admin',
          name: data.user.role?.name || data.user.roleName || 'Administrator',
          description: data.user.role?.description || '',
          tablePermissions: data.user.role?.tablePermissions || {}
        }
      });
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // 初始化时获取CSRF Token和验证token
  useEffect(() => {
    const initialize = async () => {
      await fetchCSRFToken();
      await verifyToken();
    };
    initialize();
  }, [token]);

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    token,
    csrfToken,
    apiCall,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 